import {
  addHours,
  adjustFromTimeZone,
  Container,
  ContainerPatchDTO,
  isStringUndefinedOrBlank,
  useCreateContainerMutation,
  useDeleteContainerMutation,
  usePatchUpdateContainerMutation,
  useUpdateContainerActiveMutation,
  useUpdateContainerDispatchIgnoreMaxNonPrimaryVolumeMutation,
  useUpdateContainerDnuMutation,
  useUpdateContainerDoNotDispatchMutation,
  useUpdateContainerMutation
} from "@ots-frontend/ots-data";
import {useCallback, useEffect, useState} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useApp} from "../providers/RegionProvider/RegionProvider"
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Button, Checkbox, Group, NumberInput, Text, Textarea} from "@mantine/core";
import {EntityMutationCallback} from "./useEntityMutation";

export type UseContainerUpdateProps = {}

export type UseContainerUpdateResult = {
  createContainer: (container: Partial<Container>, callback: EntityMutationCallback<Container> | undefined) => void
  updateContainer: (container: Partial<Container>, callback: EntityMutationCallback<Container> | undefined) => void
  patchUpdateContainer: (containerId: string, patch: ContainerPatchDTO, callback: EntityMutationCallback<Container> | undefined) => void
  updateContainerActive: (containerId: string, active: boolean) => void
  updateContainerDnu: (containerId: string, dnu: boolean) => void
  updateContainerDoNotDispatch: (containerId: string, doNotDispatch: boolean) => void
  updateContainerDispatchIgnoreMaxNonPrimaryVolume: (containerId: string, ignore: boolean) => void
  deleteContainer: (containerId: string, callback: EntityMutationCallback | undefined) => void

  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useContainerMutation = (props: UseContainerUpdateProps = {}): UseContainerUpdateResult => {
  const {regionId} = useApp()
  const {t} = useTranslation(["common"])

  // state
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_createContainer, {isLoading: isCreating}] = useCreateContainerMutation()
  const [_updateContainer, {isLoading: isUpdating}] = useUpdateContainerMutation()
  const [_patchUpdateContainer, {isLoading: isPatchUpdating}] = usePatchUpdateContainerMutation()
  const [_updateContainerActive, {isLoading: isUpdatingActive}] = useUpdateContainerActiveMutation();
  const [_updateContainerDnu, {isLoading: isUpdatingDnu}] = useUpdateContainerDnuMutation();
  const [_updateContainerDoNotDispatch, {isLoading: isUpdatingDoNotDispatch}] = useUpdateContainerDoNotDispatchMutation();
  const [_updateContainerDispatchIgnoreMaxNonPrimaryVolume, {isLoading: isUpdatingDispatchIgnore}] = useUpdateContainerDispatchIgnoreMaxNonPrimaryVolumeMutation()
  const [_deleteContainer, {isLoading: isDeleting}] = useDeleteContainerMutation()

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating || isPatchUpdating || isUpdatingActive || isUpdatingDnu || isUpdatingDoNotDispatch || isUpdatingDispatchIgnore)
  }, [isUpdating, isPatchUpdating, isUpdatingActive, isUpdatingDnu, isUpdatingDoNotDispatch, isUpdatingDispatchIgnore, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useContainerUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.container", "Container"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useContainerUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useContainerUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.container", "Container"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useContainerUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useContainerUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.container", "Container"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useContainerUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleCreateResult = useCallback((promise: Promise<Container>, callback: EntityMutationCallback<Container> | undefined) => {
    promise
      .then((payload) => {
        notifications.show({
          color: "green",
          title: t("common.entity.container", "Container") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback.onSuccess(payload)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })
          if (callback?.onFailure) {
            callback.onFailure()
          }
        }
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback<Container> | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.container", "Container") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (callback?.onSuccess) {
          callback?.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected?.data?.detail ?? t("common.error", "Error", {ns: "common"}),
          })

          if (callback?.onFailure) {
            callback?.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, callback: EntityMutationCallback | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.container", "Container") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (callback?.onSuccess) {
          callback?.onSuccess(undefined)
        }
      })
      .catch((rejected) => {
          notifications.show({
            color: "red",
            title: t("common.error", "Error"),
            message: rejected.data?.detail ?? t("common.error", "Error"),
          })

          if (callback?.onFailure) {
            callback?.onFailure()
          }
        }
      );
  }, [t])

  const handleDeleteConfirm = useCallback((containerId: string, callback: EntityMutationCallback | undefined) => {
    const promise = _deleteContainer({id: containerId}).unwrap()
    handleDeleteResult(promise, callback)
  }, [_deleteContainer, handleDeleteResult])

  const createContainer = useCallback((container: Partial<Container>, callback: EntityMutationCallback<Container> | undefined) => {
    const promise = _createContainer({...container}).unwrap()
    handleCreateResult(promise, callback)
  }, [_createContainer, handleCreateResult])

  const updateContainer = useCallback((container: Partial<Container>, callback: EntityMutationCallback<Container> | undefined) => {
    const promise = _updateContainer({...container}).unwrap()
    handleUpdateResult(promise, callback)
  }, [_updateContainer, handleUpdateResult])

  const patchUpdateContainer = useCallback((containerId: string, patch: ContainerPatchDTO, callback: EntityMutationCallback<Container> | undefined) => {
    const promise = _patchUpdateContainer({id: containerId, patch}).unwrap()
    handleUpdateResult(promise, callback)
  }, [_patchUpdateContainer, handleUpdateResult])

  const updateContainerActive = useCallback((containerId: string, active: boolean) => {
    if (regionId) {
      const promise = _updateContainerActive({
        containerId: containerId,
        regionId: regionId,
        active: active
      }).unwrap()
      handleUpdateResult(promise, undefined)
    }
  }, [_updateContainerActive, regionId, handleUpdateResult])

  const handleUpdateContainerDnu = useCallback((containerId: string, dnu: boolean, clearDttm: number | undefined, clearNotes: string | undefined, callback: EntityMutationCallback<Container> | undefined) => {
    if (regionId) {
      const promise = _updateContainerDnu({
        containerId: containerId,
        regionId: regionId,
        dnu: dnu,
        clearDttm: clearDttm,
        clearNotes: clearNotes,
      }).unwrap()
      handleUpdateResult(promise, callback)
    }
  }, [_updateContainerDnu, regionId, handleUpdateResult])

  const handleUpdateContainerDoNotDispatch = useCallback((containerId: string, doNotDispatch: boolean, clearDttm: number | undefined, clearNotes: string | undefined, callback: EntityMutationCallback<Container> | undefined) => {
    if (regionId) {
      const promise = _updateContainerDoNotDispatch({
        containerId: containerId,
        regionId: regionId,
        doNotDispatch: doNotDispatch,
        clearDttm: clearDttm,
        clearNotes: clearNotes,
      }).unwrap()
      handleUpdateResult(promise, callback)
    }
  }, [_updateContainerDoNotDispatch, regionId, handleUpdateResult])

  const updateContainerDispatchIgnoreMaxNonPrimaryVolume = useCallback((containerId: string, ignore: boolean) => {
    if (regionId) {
      const promise = _updateContainerDispatchIgnoreMaxNonPrimaryVolume({
        containerId: containerId,
        regionId: regionId,
        ignore: ignore
      }).unwrap()
      handleUpdateResult(promise, undefined)
    }
  }, [_updateContainerDispatchIgnoreMaxNonPrimaryVolume, regionId, handleUpdateResult])

  const updateContainerDnu = useCallback((containerId: string, dnu: boolean) => {
    if (dnu) {
      modals.open({
        title: t("common.action.update", "Update") + " " + t("common.entity.container", "Container"),
        centered: true,
        children: (
          <ResetModalContent
            label={t<string>("common.action.entity.container.scheduleddnu", "Reset container to usable status after certain hours?")}
            onCancel={modals.closeAll}
            onSubmit={(dttm, notes) => {
              handleUpdateContainerDnu(containerId, dnu, dttm, notes, {onSuccess: modals.closeAll})
            }}
          />
        )
      })
    } else {
      handleUpdateContainerDnu(containerId, dnu, undefined, undefined, undefined)
    }
  }, [handleUpdateContainerDnu, t])

  const updateContainerDoNotDispatch = useCallback((containerId: string, doNotDispatch: boolean) => {
    if (doNotDispatch) {
      modals.open({
        title: t("common.action.update", "Update") + " " + t("common.entity.container", "Container"),
        centered: true,
        children: (
          <ResetModalContent
            label={t<string>("common.action.entity.container.scheduleddnd", "Reset container to dispatchable status after certain hours?")}
            onCancel={modals.closeAll}
            onSubmit={(dttm, notes) => {
              handleUpdateContainerDoNotDispatch(containerId, doNotDispatch, dttm, notes, {onSuccess: modals.closeAll})
            }}
          />
        )
      })
    } else {
      handleUpdateContainerDoNotDispatch(containerId, doNotDispatch, undefined, undefined, undefined)
    }
  }, [handleUpdateContainerDoNotDispatch, t])

  const deleteContainer = useCallback((dispatchId: string, callback: EntityMutationCallback | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.container", "Container"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deletecontainer.confirm", "Are you sure you want to delete this container?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(dispatchId, callback)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    createContainer: createContainer,
    updateContainer: updateContainer,
    patchUpdateContainer: patchUpdateContainer,
    updateContainerActive: updateContainerActive,
    updateContainerDnu: updateContainerDnu,
    updateContainerDoNotDispatch: updateContainerDoNotDispatch,
    updateContainerDispatchIgnoreMaxNonPrimaryVolume: updateContainerDispatchIgnoreMaxNonPrimaryVolume,
    deleteContainer: deleteContainer,
  }
}

const ResetModalContent = (props: {
  label: string,
  onCancel: () => void,
  onSubmit: (clearDttm: number | undefined, clearNotes: string | undefined) => void
}) => {
  const {label, onCancel, onSubmit} = props

  const [clear, setClear] = useState<boolean>(false);
  const [clearNote, setClearNote] = useState<string>('');
  const [clearHours, setClearHours] = useState<number | ''>('');

  const {t} = useTranslation(["common"])

  return (
    <>
      <Checkbox
        label={label}
        checked={clear}
        onChange={(e) => setClear(e.target.checked)}
      />

      {clear &&
        <>
          <NumberInput
            label={t("common.action.resetafterhours", "Reset after (hours)")}
            value={clearHours}
            onChange={setClearHours}
            min={1}
            max={168}
            step={1}
            placeholder={t("common.hours.placeholder", "Enter hours")}
          />

          <Textarea
            label="Notes"
            value={clearNote}
            onChange={(e) => setClearNote(e.target.value)}
          />
        </>
      }

      <Group position="center" grow={true}>
        <Button fullWidth mt="xl" onClick={onCancel}>
          {t("common.action.cancel", "Cancel", {ns: "common"})}
        </Button>
        <Button
          type="submit"
          fullWidth mt="xl"
          onClick={() => {
            let notes: string | undefined = undefined
            let clearDttm: number | undefined = undefined

            if (clear) {
              if (!isStringUndefinedOrBlank(clearNote)) {
                notes = clearNote
              }
              if (clearHours) {
                clearDttm = adjustFromTimeZone(addHours(new Date(), clearHours)).getTime()
              }
            }

            onSubmit(clearDttm, notes)
          }}
        >
          {t("common.action.submit", "Submit", {ns: "common"})}
        </Button>
      </Group>
    </>
  )
}
